import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Medal, Award, TrendingUp } from "lucide-react";

type LeaderboardEntry = {
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatar: string | null;
    kudozBalance: number | null;
    lifetimeKudozEarned: number | null;
  };
  rank: number;
  totalKudoz: number;
  categoryKudoz?: number;
};

type UserRank = {
  rank: number;
  totalKudoz: number;
} | null;

const categories = [
  { value: 'all', label: 'All Kudoz' },
  { value: 'order', label: 'Orders' },
  { value: 'review', label: 'Reviews' },
  { value: 'badge', label: 'Badges' },
  { value: 'gift_certificate', label: 'Referrals' },
  { value: 'service', label: 'Service' },
];

const timeframes = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'all', label: 'All Time' },
];

export default function Leaderboard() {
  const [category, setCategory] = useState('all');
  const [timeframe, setTimeframe] = useState<'week' | 'month' | 'all'>('all');

  const { data: leaderboard, isLoading } = useQuery<LeaderboardEntry[]>({
    queryKey: ['/api/leaderboards', { category: category === 'all' ? undefined : category, timeframe, limit: 100 }],
  });

  const { data: userRank } = useQuery<UserRank>({
    queryKey: ['/api/leaderboards/rank', { category: category === 'all' ? undefined : category, timeframe }],
  });

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" data-testid="icon-trophy-1" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" data-testid="icon-medal-2" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" data-testid="icon-medal-3" />;
    return <Award className="w-5 h-5 text-muted-foreground" data-testid={`icon-award-${rank}`} />;
  };

  return (
    <div className="container max-w-4xl mx-auto p-4 space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-primary" data-testid="icon-trending" />
          <div>
            <h1 className="text-3xl font-bold" data-testid="text-title">Leaderboard</h1>
            <p className="text-muted-foreground" data-testid="text-subtitle">See who's earning the most Kudoz</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[200px]" data-testid="select-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value} data-testid={`select-category-${cat.value || 'all'}`}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={timeframe} onValueChange={(val) => setTimeframe(val as 'week' | 'month' | 'all')}>
            <SelectTrigger className="w-[180px]" data-testid="select-timeframe">
              <SelectValue placeholder="Timeframe" />
            </SelectTrigger>
            <SelectContent>
              {timeframes.map((tf) => (
                <SelectItem key={tf.value} value={tf.value} data-testid={`select-timeframe-${tf.value}`}>
                  {tf.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* User's Current Rank */}
      {userRank && (
        <Card className="border-primary" data-testid="card-user-rank">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Your Rank
              {getRankIcon(userRank.rank)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-2xl font-bold" data-testid="text-user-rank">#{userRank.rank}</p>
                <p className="text-sm text-muted-foreground">
                  {timeframe === 'week' ? 'This Week' : timeframe === 'month' ? 'This Month' : 'All Time'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary" data-testid="text-user-kudoz">
                  {userRank.totalKudoz.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Kudoz Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard List */}
      <Card data-testid="card-leaderboard">
        <CardHeader>
          <CardTitle>Top Earners</CardTitle>
          <CardDescription>
            {categories.find(c => c.value === category)?.label || 'All Kudoz'} - {' '}
            {timeframes.find(t => t.value === timeframe)?.label}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-md">
                  <Skeleton className="w-8 h-8" />
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              ))
            ) : leaderboard && leaderboard.length > 0 ? (
              leaderboard.map((entry) => (
                <div
                  key={entry.user.id}
                  className="flex items-center gap-4 p-3 rounded-md hover-elevate active-elevate-2"
                  data-testid={`row-leaderboard-${entry.rank}`}
                >
                  {/* Rank */}
                  <div className="flex items-center justify-center w-8">
                    {entry.rank <= 3 ? (
                      getRankIcon(entry.rank)
                    ) : (
                      <span className="text-sm font-bold text-muted-foreground" data-testid={`text-rank-${entry.rank}`}>
                        #{entry.rank}
                      </span>
                    )}
                  </div>

                  {/* Avatar */}
                  <Avatar data-testid={`avatar-user-${entry.user.id}`}>
                    <AvatarImage src={entry.user.avatar || undefined} />
                    <AvatarFallback>
                      {(entry.user.displayName || entry.user.username).substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate" data-testid={`text-username-${entry.user.id}`}>
                      {entry.user.displayName || entry.user.username}
                    </p>
                    <p className="text-sm text-muted-foreground truncate" data-testid={`text-handle-${entry.user.id}`}>
                      @{entry.user.username}
                    </p>
                  </div>

                  {/* Kudoz */}
                  <div className="text-right">
                    <Badge variant="secondary" className="text-base font-bold" data-testid={`badge-kudoz-${entry.user.id}`}>
                      {category !== 'all' && entry.categoryKudoz !== undefined
                        ? entry.categoryKudoz.toLocaleString()
                        : entry.totalKudoz.toLocaleString()}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">Kudoz</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground" data-testid="text-no-data">
                No leaderboard data available yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
